import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { Activity, TrendingUp, Award, Target, Zap, Heart } from 'lucide-react';
import GlassCard from '../components/UI/GlassCard';
import FloatingElement from '../components/UI/FloatingElement';

const NutritionAnalysis: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('week');

  // Mock data for nutrition analysis
  const macroData = [
    { name: 'Protein', value: 25, color: '#8b5cf6', target: 30 },
    { name: 'Carbs', value: 45, color: '#ec4899', target: 40 },
    { name: 'Fat', value: 30, color: '#06b6d4', target: 30 }
  ];

  const weeklyData = [
    { day: 'Mon', calories: 1800, protein: 120, carbs: 200, fat: 60 },
    { day: 'Tue', calories: 2100, protein: 140, carbs: 250, fat: 70 },
    { day: 'Wed', calories: 1950, protein: 130, carbs: 220, fat: 65 },
    { day: 'Thu', calories: 2200, protein: 150, carbs: 280, fat: 75 },
    { day: 'Fri', calories: 1850, protein: 125, carbs: 210, fat: 58 },
    { day: 'Sat', calories: 2300, protein: 160, carbs: 300, fat: 80 },
    { day: 'Sun', calories: 1900, protein: 135, carbs: 230, fat: 62 }
  ];

  const vitaminData = [
    { vitamin: 'Vitamin A', value: 85, fullMark: 100 },
    { vitamin: 'Vitamin C', value: 92, fullMark: 100 },
    { vitamin: 'Vitamin D', value: 45, fullMark: 100 },
    { vitamin: 'Vitamin E', value: 78, fullMark: 100 },
    { vitamin: 'B12', value: 88, fullMark: 100 },
    { vitamin: 'Folate', value: 72, fullMark: 100 }
  ];

  const healthMetrics = [
    { 
      title: 'Overall Health Score', 
      value: 87, 
      icon: <Award className="w-6 h-6" />, 
      color: 'from-green-500 to-emerald-500',
      description: 'Excellent nutritional balance'
    },
    { 
      title: 'Hydration Level', 
      value: 75, 
      icon: <Activity className="w-6 h-6" />, 
      color: 'from-blue-500 to-cyan-500',
      description: 'Good water intake'
    },
    { 
      title: 'Energy Balance', 
      value: 92, 
      icon: <Zap className="w-6 h-6" />, 
      color: 'from-yellow-500 to-orange-500',
      description: 'Optimal calorie distribution'
    },
    { 
      title: 'Heart Health', 
      value: 89, 
      icon: <Heart className="w-6 h-6" />, 
      color: 'from-red-500 to-pink-500',
      description: 'Cardiovascular-friendly diet'
    }
  ];

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <FloatingElement delay={0}>
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center">
              <TrendingUp className="w-10 h-10 text-white" />
            </div>
          </FloatingElement>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Nutrition <span className="bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent">
              Analysis
            </span>
          </h1>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">
            Comprehensive nutritional insights with AI-powered recommendations, 
            real-time health metrics, and personalized optimization strategies.
          </p>
        </motion.div>

        {/* Time Frame Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8"
        >
          <GlassCard className="p-6">
            <div className="flex justify-center space-x-4">
              {['day', 'week', 'month'].map((timeframe) => (
                <button
                  key={timeframe}
                  onClick={() => setSelectedTimeframe(timeframe)}
                  className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                    selectedTimeframe === timeframe
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                      : 'bg-white/10 text-purple-200 hover:bg-white/20'
                  }`}
                >
                  {timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}
                </button>
              ))}
            </div>
          </GlassCard>
        </motion.div>

        {/* Health Metrics Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {healthMetrics.map((metric, index) => (
            <FloatingElement key={metric.title} delay={index * 0.1}>
              <GlassCard className="p-6 text-center" glow>
                <div className={`w-12 h-12 mx-auto mb-4 bg-gradient-to-r ${metric.color} rounded-full flex items-center justify-center text-white`}>
                  {metric.icon}
                </div>
                <h3 className="text-white font-semibold mb-2">{metric.title}</h3>
                <div className={`text-3xl font-bold mb-2 ${getScoreColor(metric.value)}`}>
                  {metric.value}%
                </div>
                <p className="text-purple-300 text-sm">{metric.description}</p>
                
                {/* Progress bar */}
                <div className="w-full bg-gray-700 rounded-full h-2 mt-4">
                  <motion.div
                    className={`h-2 rounded-full bg-gradient-to-r ${metric.color}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${metric.value}%` }}
                    transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                  />
                </div>
              </GlassCard>
            </FloatingElement>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Macronutrient Distribution */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <Target className="w-6 h-6 text-purple-400 mr-2" />
                Macronutrient Balance
              </h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={macroData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {macroData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0,0,0,0.8)', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: 'white'
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                {macroData.map((macro) => (
                  <div key={macro.name} className="text-center">
                    <div className="flex items-center justify-center space-x-2 mb-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: macro.color }} />
                      <span className="text-white text-sm">{macro.name}</span>
                    </div>
                    <div className="text-lg font-bold text-white">{macro.value}%</div>
                    <div className="text-xs text-purple-300">Target: {macro.target}%</div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </motion.div>

          {/* Weekly Nutrition Trends */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <TrendingUp className="w-6 h-6 text-green-400 mr-2" />
                Weekly Trends
              </h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="day" stroke="#a855f7" />
                    <YAxis stroke="#a855f7" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0,0,0,0.8)', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: 'white'
                      }} 
                    />
                    <Bar dataKey="calories" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </GlassCard>
          </motion.div>
        </div>

        {/* Vitamin & Mineral Analysis */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mb-8"
        >
          <GlassCard className="p-6">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center">
              <Zap className="w-6 h-6 text-yellow-400 mr-2" />
              Vitamin & Mineral Profile
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={vitaminData}>
                    <PolarGrid stroke="rgba(255,255,255,0.2)" />
                    <PolarAngleAxis dataKey="vitamin" tick={{ fill: '#a855f7', fontSize: 12 }} />
                    <PolarRadiusAxis 
                      angle={90} 
                      domain={[0, 100]} 
                      tick={{ fill: '#a855f7', fontSize: 10 }}
                    />
                    <Radar
                      name="Vitamins"
                      dataKey="value"
                      stroke="#8b5cf6"
                      fill="#8b5cf6"
                      fillOpacity={0.3}
                      strokeWidth={2}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-4">
                {vitaminData.map((vitamin, index) => (
                  <div key={vitamin.vitamin} className="flex items-center justify-between">
                    <span className="text-white font-medium">{vitamin.vitamin}</span>
                    <div className="flex items-center space-x-3">
                      <div className="w-24 bg-gray-700 rounded-full h-2">
                        <motion.div
                          className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                          initial={{ width: 0 }}
                          animate={{ width: `${vitamin.value}%` }}
                          transition={{ duration: 1, delay: 0.7 + index * 0.1 }}
                        />
                      </div>
                      <span className={`text-sm font-semibold ${getScoreColor(vitamin.value)}`}>
                        {vitamin.value}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* AI Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <GlassCard className="p-6">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center">
              <Award className="w-6 h-6 text-yellow-400 mr-2" />
              AI-Powered Recommendations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <h3 className="text-green-300 font-semibold mb-2">✅ Strengths</h3>
                <ul className="text-green-200 text-sm space-y-1">
                  <li>• Excellent protein intake</li>
                  <li>• Good vitamin C levels</li>
                  <li>• Balanced meal timing</li>
                </ul>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                <h3 className="text-yellow-300 font-semibold mb-2">⚠️ Areas to Improve</h3>
                <ul className="text-yellow-200 text-sm space-y-1">
                  <li>• Increase vitamin D intake</li>
                  <li>• Add more omega-3 sources</li>
                  <li>• Boost fiber consumption</li>
                </ul>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <h3 className="text-blue-300 font-semibold mb-2">💡 Suggestions</h3>
                <ul className="text-blue-200 text-sm space-y-1">
                  <li>• Add fatty fish twice weekly</li>
                  <li>• Include more leafy greens</li>
                  <li>• Consider vitamin D supplement</li>
                </ul>
              </div>
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
};

export default NutritionAnalysis;